import { useGoogleMap } from "#/hook/useGoogleMap";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { IconMapPinFilled } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useClickAway } from "react-use";

const getMarkerIconSvg = (color: string) => {
  const svg = ReactDOMServer.renderToStaticMarkup(
    <IconMapPinFilled color={color} size={20} />
  );
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

export interface Location {
  latitude: number | string | null;
  longitude: number | string | null;
}

export interface MapMarker extends Location {
  color: string;
  [key: string]: unknown;
  infoWindowContent?: () => React.ReactNode;
}

export interface InventoryMapProps {
  markers: MapMarker[];
  zoom: number;
  additionalComponent?: React.ReactNode;
  options?: google.maps.MapOptions;
}

const InventoryMap: React.FC<InventoryMapProps> = ({
  markers,
  zoom,
  options,
  additionalComponent,
}) => {
  const { isLoaded } = useGoogleMap();

  const [selectedMarker, setSelectedMarker] = useState<(MapMarker & { latitude: number; longitude: number }) | null>(null);
  const infoWindowRef = useRef(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Convert and filter markers with invalid coordinates
  const validMarkers = markers
    .map((marker) => ({
      ...marker,
      latitude: typeof marker.latitude === 'string' ? parseFloat(marker.latitude) : marker.latitude,
      longitude: typeof marker.longitude === 'string' ? parseFloat(marker.longitude) : marker.longitude,
    }))
    .filter(
      (marker) =>
        marker.latitude !== null &&
        marker.latitude !== undefined &&
        marker.latitude !== 0 &&
        typeof marker.latitude === 'number' &&
        !isNaN(marker.latitude) &&
        marker.longitude !== null &&
        marker.longitude !== undefined &&
        marker.longitude !== 0 &&
        typeof marker.longitude === 'number' &&
        !isNaN(marker.longitude)
    ) as (MapMarker & { latitude: number; longitude: number })[];

  useClickAway(infoWindowRef, () => {
    setSelectedMarker(null);
  });

  // Auto-pan map to show selected marker and popup
  useEffect(() => {
    if (selectedMarker && mapRef.current) {
      const map = mapRef.current;
      const position = {
        lat: selectedMarker.latitude,
        lng: selectedMarker.longitude,
      };

      // Pan to marker with slight delay for smooth animation
      setTimeout(() => {
        map.panTo(position);
      }, 100);
    }
  }, [selectedMarker]);

  // Default center for when there are no valid markers
  const defaultCenter = { lat: 0, lng: 0 };
  const mapCenter = validMarkers.length > 0
    ? { lat: validMarkers[0].latitude, lng: validMarkers[0].longitude }
    : defaultCenter;

  return (
    <div className="relative w-full h-full">
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={validMarkers.length > 0 ? zoom : 2}
          onLoad={(map) => {
            mapRef.current = map;
            if (validMarkers.length > 0) {
              const bounds = new google.maps.LatLngBounds();
              for (const m of validMarkers) {
                bounds.extend({ lat: m.latitude, lng: m.longitude });
              }
              if (!bounds.isEmpty()) {
                map.fitBounds(bounds);
              }
            }
          }}
          options={options || {}}
        >
          {validMarkers.map((marker, index) => (
            <Marker
              key={`${marker.longitude}_${marker.latitude}_${index}`}
              position={{
                lat: marker.latitude,
                lng: marker.longitude,
              }}
              icon={{
                url: getMarkerIconSvg(marker.color),
                scaledSize: new google.maps.Size(20, 20),
                anchor: new google.maps.Point(10, 20),
              }}
              onClick={() => setSelectedMarker(marker)}
            />
          ))}

          {selectedMarker && (
            <OverlayView
              position={{
                lat: selectedMarker.latitude,
                lng: selectedMarker.longitude,
              }}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                ref={infoWindowRef}
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, calc(-100% - 30px))',
                  maxWidth: '320px',
                  zIndex: 1000,
                }}
              >
                {selectedMarker?.infoWindowContent?.()}
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      )}
      {additionalComponent && additionalComponent}
    </div>
  );
};

export default React.memo(InventoryMap);
